import { useEffect, useMemo, useState } from 'react';
import { Ruler, Plus, Trash2 } from 'lucide-react';
import { Button, Dialog, Input, cn } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import {
  addPart,
  removePart,
  splitEqually,
  splitSummary,
  toCm,
  withFreeSpace,
  type SplitPart,
} from '../model/split-bay.js';

/**
 * QATORNI O'LCHAMLARGA BO'LISH — javonni mahsulotga TAYYORLASH.
 *
 * Katak kartochkasidagi taqsimlash bitta o'rin va bitta mahsulot haqida.
 * Bu esa boshqa ish: omborchi javonni hali hech narsa qo'ymasdan turib
 * o'lchab chiqadi — "shu taxtani beshta ellik santimetrlik joyga bo'l",
 * "pastki qatorni uchga bo'l". Mahsulot keyin, har o'ringa alohida
 * biriktiriladi.
 *
 * Shuning uchun bu yerda MAHSULOT SO'RALMAYDI: har o'rinni tanlashga
 * majburlash javonni o'lchashni mumkin bo'lmagan darajada uzaytirardi.
 *
 * Qatorning UMUMIY UZUNLIGI o'zgarmaydi — u javonning o'zi. Taqsimlanmagan
 * joy oxirgi o'rin bo'lib qo'shiladi.
 */
export interface LevelSplitDialogProps {
  open: boolean;
  onClose: () => void;
  /** Stellaj kodi — sarlavhada. */
  rackCode: string;
  /** Qator raqami, 1 dan boshlanadi. */
  level: number;
  /** Qatordagi hozirgi bo'linish, metr. */
  currentM: readonly number[];
  onSubmit: (partsCm: number[]) => void;
  pending?: boolean;
  /**
   * Server xatosi — oyna YOPILMAYDI. Eng ko'p uchraydigani 409: o'chib
   * ketadigan katakda mahsulot bor. Uni faqat "toast" bilan aytish
   * o'lchamlarni qaytadan terishga majbur qilardi.
   */
  error?: string | null;
}

/** Chizmadagi eng kichik ko'rinadigan ulush — 1 sm li o'rin ham bilinsin. */
const MIN_SHARE = 0.04;

const fromMetres = (row: readonly number[]): SplitPart[] =>
  row.map((width) => ({ widthCm: toCm(width), nomenclatureIds: [] }));

export const LevelSplitDialog = ({
  open,
  onClose,
  rackCode,
  level,
  currentM,
  onSubmit,
  pending = false,
  error = null,
}: LevelSplitDialogProps) => {
  const rowCm = currentM.reduce((total, width) => total + toCm(width), 0);

  /* Oyna qatorning HOZIRGI bo'linishidan boshlanadi: ko'p holatda bitta
     o'lcham tuzatiladi, hammasi qaytadan terilmaydi. */
  const [parts, setParts] = useState<SplitPart[]>(() => fromMetres(currentM));
  useEffect(() => {
    if (open) setParts(fromMetres(currentM));
    // `currentM` har chizilishda yangi massiv bo'lib keladi — uzunligi va
    // yig'indisi o'zgarmasa qoralamani qayta tiklash noto'g'ri bo'lardi.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, rowCm, currentM.length]);

  const summary = useMemo(() => splitSummary(parts, rowCm), [parts, rowCm]);
  const final = useMemo(() => withFreeSpace(parts, rowCm), [parts, rowCm]);

  const setWidth = (index: number, widthCm: number): void => {
    setParts((current) =>
      current.map((part, i) => (i === index ? { ...part, widthCm } : part)),
    );
  };

  const totalShare = Math.max(summary.sumCm, rowCm);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t('warehouse.levelSplitTitle', { rack: rackCode, level })}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('warehouse.cancel')}
          </Button>
          <Button
            disabled={!summary.valid}
            loading={pending}
            onClick={() => onSubmit(final.map((part) => part.widthCm))}
          >
            {t('warehouse.splitSubmit', { count: final.length })}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error === null ? null : (
          <p
            role="alert"
            className={cn(
              'border-danger/25 bg-danger-soft text-danger rounded-[var(--radius-sm)]',
              'border px-3 py-2 text-sm',
            )}
          >
            {error}
          </p>
        )}

        <p className="text-text-muted text-sm">
          {t('warehouse.levelSplitIntro', { width: rowCm })}
        </p>

        {/* CHIZMA — haqiqiy nisbatda: 130 sm li o'rin ekranda ham 30 sm
            likdan to'rt baravar keng bo'lishi kerak. */}
        <div
          aria-hidden
          className="border-border bg-surface-2 flex gap-[3px] rounded-[var(--radius-sm)] border p-[3px]"
        >
          {parts.map((part, index) => (
            <div
              // Qismning o'zida identifikator yo'q — tartib raqami yagona belgisi.
              key={index}
              style={{ flexGrow: Math.max(part.widthCm / totalShare, MIN_SHARE) }}
              className={cn(
                'flex min-w-0 flex-col items-center justify-center gap-0.5 rounded-[2px]',
                'bg-[var(--color-fill-empty)] px-1 py-3 text-center',
              )}
            >
              <span className="text-text truncate font-mono text-[0.6875rem] tabular-nums">
                {String(index + 1).padStart(2, '0')}
              </span>
              <span className="text-text-muted truncate text-[0.625rem] tabular-nums">
                {part.widthCm} sm
              </span>
            </div>
          ))}

          {summary.freeCm > 0 ? (
            <div
              style={{ flexGrow: Math.max(summary.freeCm / totalShare, MIN_SHARE) }}
              className={cn(
                'border-border text-text-faint flex min-w-0 items-center justify-center',
                'rounded-[2px] border border-dashed px-1 py-3 text-[0.625rem] tabular-nums',
              )}
            >
              {summary.freeCm} sm
            </div>
          ) : null}
        </div>

        {/* TEZ TANLOV birinchi o'rinda: qatorni o'lchashning eng ko'p
            uchraydigan yo'li — teng bo'lish. */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-text-muted text-xs">{t('warehouse.splitEqualLabel')}</span>
          {[2, 3, 4, 5, 6, 8].map((count) => (
            <Button
              key={count}
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setParts((current) => splitEqually(current, rowCm, count))}
            >
              {count}
            </Button>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {parts.map((part, index) => (
            <div key={index} className="flex items-end gap-1">
              <Input
                mono
                type="number"
                min={1}
                step={5}
                className="min-w-0 flex-1"
                label={t('warehouse.levelSplitPartLabel', { index: index + 1 })}
                suffix={t('builder.unitCm')}
                value={String(part.widthCm)}
                onChange={(event) =>
                  setWidth(index, Math.round(Number(event.target.value)) || 0)
                }
              />
              <button
                type="button"
                disabled={parts.length <= 1}
                onClick={() => setParts((current) => removePart(current, index))}
                aria-label={t('warehouse.splitRemovePart', { index: index + 1 })}
                className={cn(
                  'mb-1 flex size-9 shrink-0 items-center justify-center',
                  'rounded-[var(--radius-sm)] text-text-faint',
                  'transition-[background-color,color] duration-[var(--motion-fast)]',
                  'hover:bg-danger-soft hover:text-danger',
                  'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent',
                  'disabled:hover:text-text-faint',
                )}
              >
                <Trash2 size={15} aria-hidden />
              </button>
            </div>
          ))}
        </div>

        <Button
          type="button"
          variant="secondary"
          size="sm"
          className="self-start"
          disabled={summary.freeCm < 1}
          onClick={() => setParts((current) => addPart(current, rowCm))}
        >
          <Plus size={14} aria-hidden />
          {t('warehouse.levelSplitAddPart')}
        </Button>

        <p
          aria-live="polite"
          className={cn(
            'tabular flex items-center gap-2 text-sm',
            summary.freeCm < 0 ? 'text-danger' : 'text-text-muted',
          )}
        >
          <Ruler size={14} aria-hidden className="shrink-0" />
          {summary.freeCm < 0
            ? t('warehouse.splitOver', { over: -summary.freeCm })
            : summary.freeCm === 0
              ? t('warehouse.splitFull', { count: parts.length })
              : t('warehouse.splitFree', { free: summary.freeCm })}
        </p>

        <p className="text-text-faint text-xs">{t('warehouse.levelSplitNote', { level })}</p>
      </div>
    </Dialog>
  );
};
